import type {
  CallScheduleEntry,
  InstrumentApiResponse,
  ReferenceDataField,
} from '@moment/trading-types/instrument-data'

import {
  type CountryDomicile,
  type CountryIssue,
  type CountryOfRisk,
  type MoodysRating,
  TreasurySubtype,
} from '@moment/trading-types/reference-data'

import { convertNumberToMoodysRating } from '@/utils/moodysRatings'
import {
  COUNTRY_LABELS,
  formatBondType,
  formatBps,
  formatBpsUpTo,
  formatQuantity,
  formatYield,
} from '@/utils/presentation/bonds'
import {
  convertToTitleCase,
  defaultKeyTransform,
  defaultValueTransform,
  formatBoolean,
  formatDate,
  formatDecimal,
  formatMoney,
  formatNumber,
  formatPercentage,
  formatPrice,
  formatTime,
  PLACEHOLDER,
  pluralize,
} from '@/utils/presentation/formatting'
import { convertNumberToSpRating } from '@/utils/spRatings'
import { getUSARegionLabels } from '@/utils/usa-region-options'

export type MarketDataField =
  | 'asks_price'
  | 'asks_ytw'
  | 'asks_qty'
  | 'asks_min_quantity'
  | 'bids_price'
  | 'bids_ytw'
  | 'bids_qty'
  | 'bids_min_quantity'

type ReferenceDataTransformationConfig = {
  [R in ReferenceDataField]: {
    keyTransformation: (key: string) => string
    valueTransformation: (value: InstrumentApiResponse[R]) => string
  }
}

type MarketDataTransformationConfig = {
  [M in MarketDataField]: {
    keyTransformation: (key: string) => string
    valueTransformation: (value: number) => string
  }
}

export const DEFAULT_TRANSFORMS = {
  keyTransformation: defaultKeyTransform,
  valueTransformation: defaultValueTransform,
}

export function getInstrumentDataFieldFormatter(
  field: MarketDataField | ReferenceDataField,
) {
  if (field in referenceDataTransformationConfig) {
    return referenceDataTransformationConfig[field as ReferenceDataField]
      .keyTransformation
  } else if (field in marketDataTransformationConfig) {
    return marketDataTransformationConfig[field as MarketDataField]
      .keyTransformation
  } else {
    return DEFAULT_TRANSFORMS.keyTransformation
  }
}

export function getInstrumentDataValueTransformer(
  field: MarketDataField | ReferenceDataField,
) {
  if (field in referenceDataTransformationConfig) {
    return referenceDataTransformationConfig[field as ReferenceDataField]
      .valueTransformation
  } else if (field in marketDataTransformationConfig) {
    return marketDataTransformationConfig[field as MarketDataField]
      .valueTransformation
  } else {
    return DEFAULT_TRANSFORMS.valueTransformation
  }
}

export const referenceDataTransformationConfig: ReferenceDataTransformationConfig =
  {
    // Additional fields from 78 field list
    accrued_interest: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatMoney,
    },
    accrued_interest_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    accrued_interest_pop: {
      keyTransformation: (_key) => 'Accrued interest (% of par)',
      valueTransformation: (value: number | null) => {
        return formatPercentage(value, { numDigits: 3 })
      },
    },
    ai_issuer: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: defaultValueTransform,
    },
    amount_outstanding: {
      keyTransformation: (_key) => 'Outstanding amount',
      valueTransformation: (value: number | null) => {
        return formatPrice(value, {
          shouldTruncateIfWhole: true,
          useZeroPlaceholder: true,
        })
      },
    },
    amount_outstanding_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    asset_type: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: defaultValueTransform,
    },
    bank_qualified: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    bond_warrant: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    call_announced: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    call_frequency: DEFAULT_TRANSFORMS,
    call_notification_convention: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: defaultValueTransform,
    },
    call_notification_max: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatNumber,
    },
    call_notification_min: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatNumber,
    },
    call_schedule: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: CallScheduleEntry[]): string => {
        if (!value?.length) return defaultValueTransform()

        const formatCallScheduleEntry = (entry: CallScheduleEntry) =>
          `${formatDate(entry.call_date)} @ ${formatPrice(entry.call_price)}`

        const nextCall = value.find(
          (call) => new Date(call.call_date) > new Date(),
        )
        return nextCall
          ? formatCallScheduleEntry(nextCall)
          : defaultValueTransform()
      },
    },
    call_type: DEFAULT_TRANSFORMS,
    callable: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    close_price: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatPrice,
    },
    close_price_asof: {
      keyTransformation: (_key) => 'Close price date',
      valueTransformation: formatDate,
    },
    close_ytm: {
      keyTransformation: (_key) => 'Close YTM',
      valueTransformation: (value: number | null) => {
        return formatYield(value)
      },
    },
    close_ytw: {
      keyTransformation: (_key) => 'Close YTW',
      valueTransformation: (value: number | null) => {
        return formatYield(value)
      },
    },
    coco_event_type: {
      keyTransformation: (_key) => 'Trigger event type',
      valueTransformation: defaultValueTransform,
    },
    coco_indicator: {
      keyTransformation: (_key) => 'CoCo indicator',
      valueTransformation: formatBoolean,
    },
    coco_trigger_action: {
      keyTransformation: (_key) => 'Trigger action',
      valueTransformation: defaultValueTransform,
    },
    coco_trigger_level_type: {
      keyTransformation: (_key) => 'Trigger level type',
      valueTransformation: defaultValueTransform,
    },
    coco_trigger_level_value: {
      keyTransformation: (_key) => 'Trigger level value',
      valueTransformation: defaultValueTransform,
    },
    coco_trigger_type: {
      keyTransformation: (_key) => 'Trigger type',
      valueTransformation: defaultValueTransform,
    },
    continuously_callable_flag: {
      keyTransformation: (_key) => 'Continuously callable',
      valueTransformation: formatBoolean,
    },
    convertible: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    convexity: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: number | null) => {
        return formatDecimal(value, 4)
      },
    },
    country_domicile: {
      keyTransformation: (_key) => 'Country of domicile',
      valueTransformation: (value: CountryDomicile | null) => {
        if (!value) return defaultValueTransform(value)
        return COUNTRY_LABELS[value] ?? defaultValueTransform(value)
      },
    },
    country_issue: {
      keyTransformation: (_key) => 'Country of issue',
      valueTransformation: (value: CountryIssue | null) => {
        if (!value) return defaultValueTransform(value)
        return COUNTRY_LABELS[value] ?? defaultValueTransform(value)
      },
    },
    country_of_risk: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: CountryOfRisk | null) => {
        if (!value) return defaultValueTransform(value)
        return COUNTRY_LABELS[value] ?? defaultValueTransform(value)
      },
    },
    coupon: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: number | null) => {
        return formatPercentage(value, { numDigits: 3 })
      },
    },
    coupon_frequency: DEFAULT_TRANSFORMS,
    coupon_subtype: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: defaultValueTransform,
    },
    coupon_type: DEFAULT_TRANSFORMS,
    currency: DEFAULT_TRANSFORMS,
    currently_callable: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    cusip: {
      keyTransformation: (_key) => 'CUSIP',
      valueTransformation: defaultValueTransform,
    },
    dated_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    daycount: {
      keyTransformation: (_key) => 'Day count convention',
      valueTransformation: defaultValueTransform,
    },
    de_minimis_threshold: {
      keyTransformation: (_key) => 'De minimis price',
      valueTransformation: formatPrice,
    },
    days_to_next_sink: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatNumber,
    },
    description: DEFAULT_TRANSFORMS,
    description_short: {
      keyTransformation: (_key) => 'Description',
      valueTransformation: defaultValueTransform,
    },
    duration: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: number | null) =>
        formatNumber(value, {
          useZeroPlaceholder: false,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    duration_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    fdic_code: {
      keyTransformation: (_key) => 'FDIC code',
      valueTransformation: defaultValueTransform,
    },
    fdic_insured: {
      keyTransformation: (_key) => 'FDIC insured',
      valueTransformation: formatBoolean,
    },
    filter_lists: {
      keyTransformation: (_key) => 'Filter lists',
      valueTransformation: (value: string[] | null) =>
        value?.join(', ') ?? PLACEHOLDER,
    },
    fitch_outlook: {
      keyTransformation: (_key) => 'Fitch outlook',
      valueTransformation: convertToTitleCase,
    },
    fitch_outlook_date: {
      keyTransformation: (_key) => 'Fitch outlook date',
      valueTransformation: formatDate,
    },
    fitch_rating: {
      keyTransformation: (_key) => 'Fitch rating',
      valueTransformation: defaultValueTransform,
    },
    fitch_rating_date: {
      keyTransformation: (_key) => 'Fitch rating date',
      valueTransformation: formatDate,
    },
    fitch_underlying_rating: {
      keyTransformation: (_key) => 'Fitch underlying rating',
      valueTransformation: defaultValueTransform,
    },
    fitch_underlying_rating_date: {
      keyTransformation: (_key) => 'Fitch underlying rating date',
      valueTransformation: formatDate,
    },
    first_coupon_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    has_extraordinary_call: {
      keyTransformation: (_key) => 'Extraordinary call',
      valueTransformation: formatBoolean,
    },
    insured: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    insurer: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: defaultValueTransform,
    },
    instrument_class: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: defaultValueTransform,
    },
    is_144a: {
      keyTransformation: (_key) => 'Rule 144a',
      valueTransformation: formatBoolean,
    },
    is_amt_applicable: {
      keyTransformation: (_key) => 'AMT applicable',
      valueTransformation: formatBoolean,
    },
    is_pre_refunded: {
      keyTransformation: (_key) => 'Pre-refunded',
      valueTransformation: formatBoolean,
    },
    is_state_tax_applicable: {
      keyTransformation: (_key) => 'State taxable',
      valueTransformation: formatBoolean,
    },
    is_structured_product: {
      keyTransformation: (_key) => 'Structured product',
      valueTransformation: formatBoolean,
    },
    isin: {
      keyTransformation: (_key) => 'ISIN',
      valueTransformation: defaultValueTransform,
    },
    ice_sector_2: {
      keyTransformation: (_key) => 'ICE sector group (L2)',
      valueTransformation: defaultValueTransform,
    },
    ice_sector_3: {
      keyTransformation: (_key) => 'ICE sector category (L3)',
      valueTransformation: defaultValueTransform,
    },
    ice_sector_4: {
      keyTransformation: (_key) => 'ICE sector sub-category (L4)',
      valueTransformation: defaultValueTransform,
    },
    issue_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    issue_minimum: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: number | null) => {
        return formatPrice(value, {
          shouldTruncateIfWhole: true,
          useZeroPlaceholder: true,
        })
      },
    },
    issue_minimum_denomination: {
      keyTransformation: (_key) => 'Min denomination',
      valueTransformation: (value: number | null) => {
        return formatPrice(value, {
          shouldTruncateIfWhole: true,
          useZeroPlaceholder: true,
        })
      },
    },
    issue_price: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatPrice,
    },
    issue_size: {
      keyTransformation: (_key) => 'Issue amount',
      valueTransformation: (value: number | null) => {
        return formatPrice(value, {
          shouldTruncateIfWhole: true,
          useZeroPlaceholder: true,
        })
      },
    },
    issuer: DEFAULT_TRANSFORMS,
    last_coupon_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    // TODO: Update these to use stars once Billy's designs are ready
    liquidity_institutional_aggregate: {
      keyTransformation: (_key) => 'Institutional liquidity score',
      valueTransformation: formatNumber,
    },
    liquidity_institutional_buy: {
      keyTransformation: (_key) => 'Institutional liquidity score (bid)',
      valueTransformation: formatNumber,
    },
    liquidity_institutional_sell: {
      keyTransformation: (_key) => 'Institutional liquidity score (offer)',
      valueTransformation: formatNumber,
    },
    liquidity_micro_aggregate: {
      keyTransformation: (_key) => 'Micro liquidity score',
      valueTransformation: formatNumber,
    },
    liquidity_micro_buy: {
      keyTransformation: (_key) => 'Micro liquidity score (bid)',
      valueTransformation: formatNumber,
    },
    liquidity_micro_sell: {
      keyTransformation: (_key) => 'Micro liquidity score (offer)',
      valueTransformation: formatNumber,
    },
    liquidity_retail_aggregate: {
      keyTransformation: (_key) => 'Retail liquidity score',
      valueTransformation: formatNumber,
    },
    liquidity_retail_buy: {
      keyTransformation: (_key) => 'Retail liquidity score (bid)',
      valueTransformation: formatNumber,
    },
    liquidity_retail_sell: {
      keyTransformation: (_key) => 'Retail liquidity score (offer)',
      valueTransformation: formatNumber,
    },
    lowest_rating: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: string | null) =>
        convertNumberToSpRating(value as unknown as number) ||
        defaultValueTransform(value),
    },
    maturity_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    maturity_in_years: {
      keyTransformation: (_key) => 'Years to maturity',
      valueTransformation: (value: number | null) =>
        value == null
          ? defaultValueTransform(value)
          : `${formatNumber(value, { useZeroPlaceholder: false, maximumFractionDigits: 2 })} ${pluralize(value, 'year')}`,
    },
    maturity_year: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: number | null) =>
        formatNumber(value, {
          useZeroPlaceholder: false,
          maximumFractionDigits: 0,
          useGrouping: false,
        }),
    },
    workout_year: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: number | null) =>
        formatNumber(value, {
          useZeroPlaceholder: false,
          maximumFractionDigits: 0,
          useGrouping: false,
        }),
    },
    moodys_rating: {
      keyTransformation: (_key) => "Moody's rating",
      valueTransformation: (value: MoodysRating | null) =>
        convertNumberToMoodysRating(value as unknown as number) ||
        defaultValueTransform(value),
    },
    moodys_rating_date: {
      keyTransformation: (_key) => "Moody's rating date",
      valueTransformation: formatDate,
    },
    moodys_underlying_rating: {
      keyTransformation: (_key) => "Moody's underlying rating",
      valueTransformation: (value: MoodysRating | null) =>
        convertNumberToMoodysRating(value as unknown as number) ||
        defaultValueTransform(value),
    },
    moodys_underlying_rating_date: {
      keyTransformation: (_key) => "Moody's underlying rating date",
      valueTransformation: formatDate,
    },
    muni_sector: {
      keyTransformation: (_key) => 'Municipal sector',
      valueTransformation: defaultValueTransform,
    },
    muni_obligor_state: {
      keyTransformation: (_key) => 'Municipal obligor state',
      valueTransformation: (value: string | null) =>
        (value != null && getUSARegionLabels[value]) ||
        defaultValueTransform(value),
    },
    municipal_state: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: string | null) =>
        (value != null && getUSARegionLabels[value]) ||
        defaultValueTransform(value),
    },
    municipal_taxable_federal: {
      keyTransformation: (_key) => 'Federally taxable',
      valueTransformation: formatBoolean,
    },
    muni_tax_status: {
      keyTransformation: (_key) => 'Municipal federal tax status',
      valueTransformation: defaultValueTransform,
    },
    municipal_type: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: defaultValueTransform,
    },
    naics_sector: {
      keyTransformation: (_key) => 'NAICS sector',
      valueTransformation: convertToTitleCase,
    },
    naics_subsector: {
      keyTransformation: (_key) => 'NAICS subsector',
      valueTransformation: convertToTitleCase,
    },
    name: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: defaultValueTransform,
    },
    next_call_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    next_call_in_years: {
      keyTransformation: (_key) => 'Years to next call',
      valueTransformation: (value: number | null) =>
        value == null
          ? defaultValueTransform(value)
          : `${formatNumber(value, { useZeroPlaceholder: false, maximumFractionDigits: 2 })} ${pluralize(value, 'year')}`,
    },
    days_to_maturity_from_next_call: {
      keyTransformation: (_key) => 'Days to maturity from next call',
      valueTransformation: (value: number | null) =>
        value == null
          ? defaultValueTransform(value)
          : `${formatNumber(value, { useZeroPlaceholder: false, maximumFractionDigits: 0 })} ${pluralize(value, 'day')}`,
    },
    next_call_price: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatPrice,
    },
    next_coupon_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    next_sink_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    objective_score: DEFAULT_TRANSFORMS,
    objective_score_decomposition: DEFAULT_TRANSFORMS,
    odd_first_coupon: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: defaultValueTransform,
    },
    odd_last_coupon: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: defaultValueTransform,
    },
    original_issue_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    original_issue_price: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatPrice,
    },
    original_issue_size: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: number | null) => {
        return formatPrice(value, {
          shouldTruncateIfWhole: true,
          useZeroPlaceholder: true,
        })
      },
    },
    par_value: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: number | null) => {
        return formatPrice(value, {
          shouldTruncateIfWhole: true,
          useZeroPlaceholder: true,
        })
      },
    },
    partial_call_announced: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    perpetual: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    prev_coupon_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    principal_factor: DEFAULT_TRANSFORMS,
    private_placement: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    purpose: DEFAULT_TRANSFORMS,
    puttable: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    qcb_indicator: {
      keyTransformation: (_key) => 'QCB indicator',
      valueTransformation: formatBoolean,
    },
    reg_s: {
      keyTransformation: (_key) => 'Regulation S',
      valueTransformation: formatBoolean,
    },
    run_id: {
      keyTransformation: (_key) => 'Run ID',
      valueTransformation: defaultValueTransform,
    },
    sector: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: convertToTitleCase,
    },
    security_code: DEFAULT_TRANSFORMS,
    seniority: DEFAULT_TRANSFORMS,
    settlement_convention: DEFAULT_TRANSFORMS,
    sink_fund_redemption_method: DEFAULT_TRANSFORMS,
    sinking_fund: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    sp_creditwatch: {
      keyTransformation: (_key) => 'S&P creditwatch',
      valueTransformation: defaultValueTransform,
    },
    sp_creditwatch_date: {
      keyTransformation: (_key) => 'S&P creditwatch date',
      valueTransformation: formatDate,
    },
    sp_outlook: {
      keyTransformation: (_key) => 'S&P outlook',
      valueTransformation: defaultValueTransform,
    },
    sp_outlook_date: {
      keyTransformation: (_key) => 'S&P outlook date',
      valueTransformation: defaultValueTransform,
    },
    sp_rating: {
      keyTransformation: (_key) => 'S&P rating',
      valueTransformation: (value: string | null) =>
        convertNumberToSpRating(value as unknown as number) ||
        defaultValueTransform(value),
    },
    sp_rating_date: {
      keyTransformation: (_key) => 'S&P rating date',
      valueTransformation: formatDate,
    },
    sp_underlying_rating: {
      keyTransformation: (_key) => 'S&P underlying rating',
      valueTransformation: (value: string | null) =>
        convertNumberToSpRating(value as unknown as number) ||
        defaultValueTransform(value),
    },
    sp_underlying_rating_date: {
      keyTransformation: (_key) => 'S&P underlying rating date',
      valueTransformation: formatDate,
    },
    status: DEFAULT_TRANSFORMS,
    ticker: {
      keyTransformation: (_key) => 'Ticker',
      valueTransformation: defaultValueTransform,
    },
    trace_eligible: {
      keyTransformation: (_key) => 'TRACE eligible',
      valueTransformation: formatBoolean,
    },
    treasury_benchmark: DEFAULT_TRANSFORMS,
    treasury_subtype: {
      keyTransformation: (_key) => 'Treasury type',
      valueTransformation: (value: TreasurySubtype | null): string =>
        value && [TreasurySubtype.Strips, TreasurySubtype.Tips].includes(value)
          ? value.toUpperCase()
          : convertToTitleCase(value),
    },
    type: {
      keyTransformation: (_key) => 'Asset class',
      valueTransformation: formatBondType,
    },
    ultimate_borrower: DEFAULT_TRANSFORMS,
    ultimate_parent: DEFAULT_TRANSFORMS,
    ultimate_parent_ticker: DEFAULT_TRANSFORMS,
    updated_at: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatTime,
    },
    use_of_proceeds: DEFAULT_TRANSFORMS,
    workout_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    workout_in_years: {
      keyTransformation: (_key) => 'Years to workout',
      valueTransformation: (value: number | null) =>
        value == null
          ? defaultValueTransform(value)
          : `${formatNumber(value, { useZeroPlaceholder: false, maximumFractionDigits: 2 })} ${pluralize(value, 'year')}`,
    },
    market_spread_price: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: number | null) => formatDecimal(value, 2),
    },
    market_spread_ytw: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: number | null) => formatDecimal(value, 2),
    },
    market_spread_ytm: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: number | null) => formatDecimal(value, 2),
    },
    eval_price: {
      keyTransformation: (_key) => 'Price',
      valueTransformation: formatPrice,
    },
    eval_ytw: {
      keyTransformation: (_key) => 'Yield to worst (mid)',
      valueTransformation: formatYield,
    },
    eval_ytm: {
      keyTransformation: (_key) => 'Yield to maturity (mid)',
      valueTransformation: formatYield,
    },
    ask_eval_price_diff: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: number | null) => formatDecimal(value, 2),
    },
    ask_eval_ytw_diff: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: number | null) => formatDecimal(value, 2),
    },
    bid_eval_price_diff: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: number | null) => formatDecimal(value, 2),
    },
    bid_eval_ytw_diff: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (value: number | null) => formatDecimal(value, 2),
    },
    moodys_outlook: {
      keyTransformation: (_key) => "Moody's outlook",
      valueTransformation: defaultValueTransform,
    },
    moodys_outlook_date: {
      keyTransformation: (_key) => "Moody's outlook date",
      valueTransformation: formatDate,
    },
    mid_oas: {
      keyTransformation: (_key) => 'OAS (mid)',
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    accretion_compounding_method: DEFAULT_TRANSFORMS,
    ask_g_spread: {
      keyTransformation: (_key) => 'G-spread (ask)',
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    ask_i_spread: {
      keyTransformation: (_key) => 'I-spread (ask)',
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    ask_oas: {
      keyTransformation: (_key) => 'OAS (ask)',
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    ask_price: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatPrice,
    },
    ask_ytc: {
      keyTransformation: (_key) => 'Yield to call (ask)',
      valueTransformation: formatYield,
    },
    ask_ytm: {
      keyTransformation: (_key) => 'Yield to maturity (ask)',
      valueTransformation: formatYield,
    },
    ask_ytw: {
      keyTransformation: (_key) => 'Yield to worst (ask)',
      valueTransformation: formatYield,
    },
    ask_ytw_spread_bps: {
      keyTransformation: (_key) => 'Spread to benchmark (ask)',
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    ask_z_spread: {
      keyTransformation: (_key) => 'Z-spread (ask)',
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    benchmark_isin: DEFAULT_TRANSFORMS,
    benchmark_yield: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatYield,
    },
    best_ask_post_tax_ytw: {
      keyTransformation: (_key) => 'Post-tax yield to worst (ask)',
      valueTransformation: formatYield,
    },
    best_ask_tax_equivalent_yield: {
      keyTransformation: (_key) => 'Taxable-equivalent yield (ask)',
      valueTransformation: formatYield,
    },
    bid_g_spread: {
      keyTransformation: (_key) => 'G-spread (bid)',
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    bid_i_spread: {
      keyTransformation: (_key) => 'I-spread (bid)',
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    bid_oas: {
      keyTransformation: (_key) => 'OAS (bid)',
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    bid_price: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatPrice,
    },
    bid_ytc: {
      keyTransformation: (_key) => 'Yield to call (bid)',
      valueTransformation: formatYield,
    },
    bid_ytm: {
      keyTransformation: (_key) => 'Yield to maturity (bid)',
      valueTransformation: formatYield,
    },
    bid_ytw: {
      keyTransformation: (_key) => 'Yield to worst (bid)',
      valueTransformation: formatYield,
    },
    bid_ytw_spread_bps: {
      keyTransformation: (_key) => 'Spread to benchmark (bid)',
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    bid_z_spread: {
      keyTransformation: (_key) => 'Z-spread (bid)',
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    bond_form_type: DEFAULT_TRANSFORMS,
    bond_issuer_rv_score: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (v: number | null) => formatDecimal(v, 2),
    },
    contingent_interest_indicator: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    coupon_inflation_protected: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    coupon_summary: DEFAULT_TRANSFORMS,
    coupon_type_method: DEFAULT_TRANSFORMS,
    covered_bond_collateral: DEFAULT_TRANSFORMS,
    credit_quality_class: DEFAULT_TRANSFORMS,
    credit_sensitive_indicator: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    duration_times_spread: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (v: number | null) => formatDecimal(v, 4),
    },
    effective_call_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    effective_call_price: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatPrice,
    },
    effective_duration: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (v: number | null) => formatDecimal(v, 4),
    },
    expected_spread: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (v: number | null) => formatDecimal(v, 2),
    },
    float_benchmark: {
      keyTransformation: (_key) => 'Floating rate benchmark',
      valueTransformation: defaultValueTransform,
    },
    float_formula: DEFAULT_TRANSFORMS,
    float_formula_reset_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    float_max_rate: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (v: number | null) => formatDecimal(v, 4),
    },
    float_min_rate: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (v: number | null) => formatDecimal(v, 4),
    },
    float_reset_frequency: DEFAULT_TRANSFORMS,
    float_spread: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (v: number | null) =>
        v == null
          ? defaultValueTransform(v)
          : `${formatBpsUpTo(v * 100, 3)} bps`,
    },
    gics_sector_1: {
      keyTransformation: (_key) => 'GICS sector (L1)',
      valueTransformation: defaultValueTransform,
    },
    gics_sector_2: {
      keyTransformation: (_key) => 'GICS industry group (L2)',
      valueTransformation: defaultValueTransform,
    },
    gics_sector_3: {
      keyTransformation: (_key) => 'GICS industry (L3)',
      valueTransformation: defaultValueTransform,
    },
    gics_sector_4: {
      keyTransformation: (_key) => 'GICS sub-industry (L4)',
      valueTransformation: defaultValueTransform,
    },
    ice_sector_1: {
      keyTransformation: (_key) => 'ICE sector class (L1)',
      valueTransformation: defaultValueTransform,
    },
    increment_amount_secondary: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatNumber,
    },
    issue_increment: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatNumber,
    },
    issuer_offset: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (v: number | null) => formatDecimal(v, 2),
    },
    issuer_sector_rv_score: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (v: number | null) => formatDecimal(v, 2),
    },
    make_whole_benchmark: {
      keyTransformation: (_key) => 'Make whole benchmark',
      valueTransformation: defaultValueTransform,
    },
    make_whole_callable: {
      keyTransformation: (_key) => 'Make whole',
      valueTransformation: formatBoolean,
    },
    make_whole_end_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    make_whole_spread: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    maturity_redemption_price: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatPrice,
    },
    maturity_type: DEFAULT_TRANSFORMS,
    mid_g_spread: {
      keyTransformation: (_key) => 'G-spread (mid)',
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    mid_i_spread: {
      keyTransformation: (_key) => 'I-spread (mid)',
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    mid_ytc: {
      keyTransformation: (_key) => 'Yield to call (mid)',
      valueTransformation: formatYield,
    },
    mid_ytm: {
      keyTransformation: (_key) => 'Yield to maturity (mid)',
      valueTransformation: formatYield,
    },
    mid_ytw: {
      keyTransformation: (_key) => 'Yield to worst (mid)',
      valueTransformation: formatYield,
    },
    mid_ytw_spread_bps: {
      keyTransformation: (_key) => 'Spread to benchmark (mid)',
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    mid_z_spread: {
      keyTransformation: (_key) => 'Z-spread (mid)',
      valueTransformation: (v: number | null) =>
        v == null ? defaultValueTransform(v) : `${formatBps(v, 0)} bps`,
    },
    min_denom_amount_secondary: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatNumber,
    },
    obligor: DEFAULT_TRANSFORMS,
    prospectus_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    bullet: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    default_indicator: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    discretionary_coupon: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    emerging_status: DEFAULT_TRANSFORMS,
    impact_bond_type: DEFAULT_TRANSFORMS,
    macaulay_duration: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (v: number | null) => formatDecimal(v, 4),
    },
    mid_price: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatPrice,
    },
    refund_date: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatDate,
    },
    refund_price: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatPrice,
    },
    rating_curve_value: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (v: number | null) => formatDecimal(v, 2),
    },
    sector_offset: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (v: number | null) => formatDecimal(v, 2),
    },
    seniority_adjustment: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: (v: number | null) => formatDecimal(v, 2),
    },
    seniority_type: DEFAULT_TRANSFORMS,
    strip_type: DEFAULT_TRANSFORMS,
    sukuk_contract_type: DEFAULT_TRANSFORMS,
    sukuk_indicator: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    sukuk_type: DEFAULT_TRANSFORMS,
    survivor_option: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    symbol: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: defaultValueTransform,
    },
    trading_restrictions_type: {
      keyTransformation: (_key) => 'Trading restrictions',
      valueTransformation: defaultValueTransform,
    },
    us_on_the_run_ind: {
      keyTransformation: defaultKeyTransform,
      valueTransformation: formatBoolean,
    },
    vrdo_indicator: {
      keyTransformation: (_key) => 'Variable rate demand obligation',
      valueTransformation: formatBoolean,
    },
  }

const marketDataTransformationConfig: MarketDataTransformationConfig = {
  asks_price: {
    keyTransformation: (_key) => 'Ask price',
    valueTransformation: formatPrice,
  },
  asks_ytw: {
    keyTransformation: (_key) => 'Ask YTW',
    valueTransformation: (value: number | null) => {
      return formatYield(value)
    },
  },
  asks_qty: {
    keyTransformation: (_key) => 'Ask quantity',
    valueTransformation: formatQuantity,
  },
  asks_min_quantity: {
    keyTransformation: (_key) => 'Ask min quantity',
    valueTransformation: formatQuantity,
  },
  bids_price: {
    keyTransformation: (_key) => 'Bid price',
    valueTransformation: formatPrice,
  },
  bids_ytw: {
    keyTransformation: (_key) => 'Bid YTW',
    valueTransformation: (value: number | null) => {
      return formatYield(value)
    },
  },
  bids_qty: {
    keyTransformation: (_key) => 'Bid quantity',
    valueTransformation: formatQuantity,
  },
  bids_min_quantity: {
    keyTransformation: (_key) => 'Bid min quantity',
    valueTransformation: formatQuantity,
  },
}
